import React from 'react';
import { useNavigate } from 'react-router-dom';
import { CollectionListItem } from '../types/collection';
import { AddIcon } from './CustomIcons';
import '../styles/CollectionCard.css';

interface SystemCollectionCardProps {
  item: CollectionListItem;
  rating?: number; // 1-5 stars
  onAddToMyCollections?: (collection: CollectionListItem['collection']) => void;
}

export const SystemCollectionCard: React.FC<SystemCollectionCardProps> = ({ item, rating = 5, onAddToMyCollections }) => {
  const navigate = useNavigate();
  const { collection } = item;

  const handleClick = (e: React.MouseEvent) => {
    // Don't navigate if clicking on the add button
    if ((e.target as HTMLElement).closest('.add-to-collections-btn')) {
      return;
    }
    navigate(`/collections/${collection.id}`, { state: { collection } });
  };

  const handleAddClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onAddToMyCollections) {
      onAddToMyCollections(collection);
    }
  };

  const renderStars = () => {
    const stars = [];
    for (let i = 1; i <= 5; i++) {
      stars.push(
        <span key={i} className={`star ${i <= rating ? 'filled' : 'empty'}`}>
          ★
        </span>
      );
    }
    return stars;
  };

  const getTypeBadge = () => {
    const typeStyles = {
      company: { bg: '#E3F2FD', color: '#1976D2', label: 'By Company' },
      custom: { bg: '#E8F5E9', color: '#388E3C', label: 'Custom' },
      topic: { bg: '#FFF3E0', color: '#F57C00', label: 'By Author' },
      curated: { bg: '#F3E5F5', color: '#7B1FA2', label: 'By Pattern' },
      default: { bg: '#F5F5F5', color: '#757575', label: 'System' }
    };
    
    const style = typeStyles[collection.type] || typeStyles.default;
    return (
      <span 
        className="collection-type-badge" 
        style={{ backgroundColor: style.bg, color: style.color }}
      >
        {style.label}
      </span>
    );
  };

  const getDifficultyDots = () => {
    const difficulty = collection.stats?.difficulty || { easy: 0, medium: 0, hard: 0 };
    const { easy, medium, hard } = difficulty;
    const total = collection.stats?.totalProblems || (collection as any).totalProblems || 0;
    return (
      <div className="difficulty-dots">
        {easy > 0 && <span className="dot easy">● Easy: {easy}</span>}
        {medium > 0 && <span className="dot medium">● Medium: {medium}</span>}
        {hard > 0 && <span className="dot hard">● Hard: {hard}</span>}
        <span className="dot total">Total: {total}</span>
      </div>
    );
  };

  return (
    <div className="collection-card" onClick={handleClick}>
      <div className="collection-header">
        <h3 className="collection-title">{collection.name}</h3>
        {getTypeBadge()}
      </div>
      
      <p className="collection-description">{collection.description}</p>
      
      <div className="collection-progress">
        <div className="star-rating">
          {renderStars()}
        </div>
      </div>
      
      {getDifficultyDots()}
      
      <div className="collection-footer">
        <span className="last-practiced">
          {/* Empty span to maintain layout consistency */}
        </span>
        <button 
          className="add-to-collections-btn"
          onClick={handleAddClick}
          title="Add to My Collections"
        >
          <AddIcon size={16} />
          <span>Add to My Collections</span>
        </button>
      </div>
    </div>
  );
};